/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { MultiSelect } from 'primereact/multiselect';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';

import { CANCEL_TEXT } from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import { regionListRequest } from '../../../Api/request/regionList';
import { countryListTableConfig } from './countryListTableConfig';
import {
  createCountry,
  countryListRequest,
  updateCountry,
} from '../../../Api/request/countryList';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import './CountryList.scss';


const INITIAL_COUNTRY_FORM = {
  countryName: '',
  selectedRegions: [],
};

const getAxiosResultMessage = (result) => (
  result?.data?.response?.data?.message
  || result?.data?.response?.data?.externalMessage
  || result?.data?.message
  || ''
);

const isSuccessfulAxiosResult = (result) => (
  Number.isInteger(result?.status) && result.status >= 200 && result.status < 300
);

const renderCountryModal = ({
  dialogMode,
  form,
  availableRegions,
  fieldErrors,
  isSubmitDisabled,
  handleFormChange,
  handleCloseDialog,
  handleSubmit,
}) => createPortal(
  <div className="country-list-modal-backdrop">
    <form className="country-list-modal" onSubmit={handleSubmit}>
      <div className="country-list-modal__header">
        {dialogMode === 'edit' ? 'Edit Country' : 'Add Country'}
      </div>
      <div className="country-list-modal__body">
        <div className="country-list-modal__field" style={{ marginBottom: '15px' }}>
          <label htmlFor="country-name">
            Country Name<span className="required-asterisk">*</span>
          </label>
          <input
            id="country-name"
            name="countryName"
            type="text"
            placeholder="Enter Country Name"
            value={form.countryName}
            onChange={(val) => handleFormChange('countryName', val.target.value)}
            className={fieldErrors.countryName ? 'is-error' : ''}
          />
        </div>

        <div className="country-list-modal__field">
          <label htmlFor="region-select">
            Select Regions<span className="required-asterisk">*</span>
          </label>
          <MultiSelect
            id="region-select"
            value={form.selectedRegions}
            options={availableRegions}
            onChange={(index) => handleFormChange('selectedRegions', index.value)}
            optionLabel="regionName"
            optionValue="regionId"
            placeholder="Select Regions"
            display="chip"
            className={fieldErrors.selectedRegions ? 'p-invalid' : ''}
            style={{ width: '100%', border: fieldErrors.selectedRegions ? '1px solid #d32f2f' : '1px solid #e2e8f0' }}
            // ADD THIS LINE to push the dropdown above the modal backdrop
            panelStyle={{ zIndex: 1400 }}
          />
        </div>
      </div>
      <div className="country-list-modal__footer">
        <button
          type="button"
          className="country-list-modal__btn country-list-modal__btn--ghost"
          onClick={handleCloseDialog}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="country-list-modal__btn country-list-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const buildCountryConfig = ({ userProfile, showAddNew, total, onAddClick, hasActionAccess }) => {
  const dynamicTableConfig = countryListTableConfig.filter(col => {
    if (col.field === 'action' && !hasActionAccess) return false;
    return true;
  });

  return {
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || {},
        roleDetails: userProfile?.roleDetails || [],
      },
      handlers: { handleAddIconClick: onAddClick },
      toolBarSettings: {
        showAddNew,
        showFilterOption: false,
        showToolBarHeading: true,
        toolBarHeading: 'Country List',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: dynamicTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: dynamicTableConfig.length },
      paginationConfig: {
        page: 1,
        limit: Math.max(total || 0, 1000),
        total,
        rowsPerPageOptions: [5, 10, 25, 50],
      },
      tableScrollHeight: 'calc(100vh - 140px)',
    },
  };
};

const CountryList = () => {
  const { userProfile } = useAuth();

  // Using explicit hardcoded string so it doesn't break if MODULE_NAMES is missing
  const permissionString = 'masters:configuration:country-list';
  const permission = userProfile?.permissions?.[permissionString] || [];
  const isSuperAdmin = userProfile?.roleDetails?.some(role => role.roleName === 'Super Admin');

  const normalizedPermissions = useMemo(
    () => permission.map((action) => String(action).toLowerCase()),
    [permission],
  );

  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
      delete: ['delete', 'remove'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [mappings, setMappings] = useState([]);
  const [regionDropdownList, setRegionDropdownList] = useState([]);
  const [dialogState, setDialogState] = useState({ mode: 'add', selectedRow: null, isOpen: false });
  const [form, setForm] = useState(INITIAL_COUNTRY_FORM);
  const [fieldErrors, setFieldErrors] = useState({ countryName: false, selectedRegions: false });
  const [isLoading, setIsLoading] = useState(false);
  const [toggleMapping, setToggleMapping] = useState(null);

  const loadData = useCallback(async () => {
    if (!hasAccess('read')) return;

    try {
      const mappingRes = await UseAxios(countryListRequest(), setIsLoading);
      if (isSuccessfulAxiosResult(mappingRes)) {
        const rows = mappingRes?.data?.data || mappingRes?.data?.mappings || [];
        setMappings(rows.map(row => ({
          ...row,
          mappingId: row.mapping_id ?? row.mappingId,
          regionId: row.region_id ?? row.regionId,
          regionName: row.region_name ?? row.regionName,
          countryId: row.country_id ?? row.countryId,
          countryName: row.country_name ?? row.countryName,
          isActive: row.is_active ?? row.isActive,
        })));
      }

      const regionRes = await UseAxios(regionListRequest(), setIsLoading);
      if (isSuccessfulAxiosResult(regionRes)) {
        const rRows = regionRes?.data?.regionList || regionRes?.data?.data?.tableData || [];
        setRegionDropdownList(rRows.map(index => ({
          regionId: index.regionId ?? index.region_id,
          regionName: index.regionName ?? index.region_name,
        })));
      }
    } catch (_error) {
      setMappings([]);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenAdd = useCallback(() => {
    setForm(INITIAL_COUNTRY_FORM);
    setFieldErrors({ countryName: false, selectedRegions: false });
    setDialogState({ mode: 'add', selectedRow: null, isOpen: true });
  }, []);

  const handleOpenEdit = useCallback((mappingRow) => {
    const currentMappedRegions = mappings
      .filter(index => index.countryId === mappingRow.countryId)
      .map(index => index.regionId);

    setForm({
      countryName: mappingRow.countryName || '',
      selectedRegions: currentMappedRegions,
    });
    setFieldErrors({ countryName: false, selectedRegions: false });
    setDialogState({ mode: 'edit', selectedRow: mappingRow, isOpen: true });
  }, [mappings]);

  const handleCloseDialog = useCallback(() => {
    setForm(INITIAL_COUNTRY_FORM);
    setFieldErrors({ countryName: false, selectedRegions: false });
    setDialogState({ mode: 'add', selectedRow: null, isOpen: false });
  }, []);

  const handleFormChange = useCallback((name, value) => {
    setForm(current => ({ ...current, [name]: value }));
    if (name === 'countryName' && value.trim()) setFieldErrors(prev => ({ ...prev, countryName: false }));
    if (name === 'selectedRegions' && value.length > 0) setFieldErrors(prev => ({ ...prev, selectedRegions: false }));
  }, []);

  const isSubmitDisabled = useMemo(() => {
    return !form.countryName.trim() || form.selectedRegions.length === 0;
  }, [form]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const trimmedCountryName = form.countryName.trim();

    if (!trimmedCountryName || form.selectedRegions.length === 0) {
      setFieldErrors({
        countryName: !trimmedCountryName,
        selectedRegions: form.selectedRegions.length === 0,
      });
      toast.error('Please complete all required fields.');
      return;
    }

    const payload = {
      countryName: trimmedCountryName,
      regionIds: form.selectedRegions,
    };

    if (dialogState.mode === 'edit') {
      payload.countryId = dialogState.selectedRow.countryId;
    }

    const request = dialogState.mode === 'edit'
      ? updateCountry(payload)
      : createCountry(payload);

    const response = await UseAxios(request, setIsLoading);
    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to save country mapping.');
      return;
    }
    toast.success(dialogState.mode === 'edit' ? 'Country updated successfully.' : 'Country added successfully.');
    handleCloseDialog();
    loadData();
  }, [dialogState, handleCloseDialog, loadData, form]);

  const handleToggle = useCallback(async () => {
    if (!toggleMapping) return;

    const response = await UseAxios(
      updateCountry({
        mappingId: toggleMapping.mappingId,
        isActive: toggleMapping.isActive ? 0 : 1,
      }),
      setIsLoading,
    );

    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to update status.');
      return;
    }
    toast.success('Status updated successfully.');
    setToggleMapping(null);
    loadData();
  }, [loadData, toggleMapping]);

  const tableData = useMemo(() => mappings.map((mapping, index) => ({
    ...mapping,
    slNo: index + 1,
    status: mapping.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="country-list-actions-cell" onClick={(val) => val.stopPropagation()}>
        <div className="country-list-action-cell-inner">
          {(hasAccess('update') || hasAccess('delete')) && (
            <ControlledSwitches
              checked={Boolean(mapping.isActive)}
              switchCss="switchCss"
              handleChange={() => setToggleMapping(mapping)}
            />
          )}
          {hasAccess('update') && (
            <button
              type="button"
              className="country-list-action-btn"
              title="Edit Country Mapping"
              onClick={() => handleOpenEdit(mapping)}
            >
              <img src={EditActionIcon} alt="Edit" />
            </button>
          )}
        </div>
      </div>
    ),
  })), [hasAccess, handleOpenEdit, mappings]);

  const configuration = useMemo(() => buildCountryConfig({
    userProfile,
    showAddNew: hasAccess('create'),
    total: tableData.length,
    onAddClick: handleOpenAdd,
    hasActionAccess: hasAccess('update') || hasAccess('delete'),
  }), [handleOpenAdd, hasAccess, tableData.length, userProfile]);

  // FIX: Properly wrap the no-permission UI so it's not hidden behind the header
  if (!hasAccess('read')) {
    return (
      <div className="country-list-tu"
        style={
          {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            marginTop: '64px',
          }
        }>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>No Read Access</h2>
          <p>You do not have permission to view the Country List.</p>
          <p style={{ color: '#777', fontSize: '12px' }}>
            (Check if your user role contains the <strong>{permissionString}</strong> permission array)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="country-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />

      {dialogState.isOpen && renderCountryModal({
        dialogMode: dialogState.mode,
        form,
        availableRegions: regionDropdownList,
        fieldErrors,
        isSubmitDisabled,
        handleFormChange,
        handleCloseDialog,
        handleSubmit,
      })}

      <FormDialog
        open={Boolean(toggleMapping)}
        title={toggleMapping?.isActive ? 'Disable Mapping' : 'Enable Mapping'}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon"><img alt="Alert" src={alertIcon} /></div>
            <div className="dialog-message-text">
              {toggleMapping?.isActive ? 'Do you want to disable this mapping?' : 'Do you want to enable this mapping?'}
            </div>
          </div>
        )}
        button1={CANCEL_TEXT}
        button2={toggleMapping?.isActive ? 'Disable' : 'Enable'}
        button1Action={() => setToggleMapping(null)}
        button2Action={handleToggle}
      />
    </div>
  );
};

export default CountryList;
